/****************
 * Auth Context - Clerk-based authentication
 * Provides unified auth interface for the application
 ****************/

import { inSpeedRun } from '@/lib/speedRun';
import { track } from '@/lib/analytics';
import { identifyPostHog } from '@/lib/posthog';
import React, { createContext, useContext, useMemo, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { setClerkUserId } from '@/services/brokerCredentials';
import { getViewAsUserId, isViewingAsSomeoneElse } from '@/lib/viewAs';
import { forgetPersonalBrowserState } from '@/lib/browserState';
import { isAdminEmail } from '@/lib/admins';
import { entitlementsApi } from '@/api/client';
import { isTier, tierAllows, type Feature, type Tier } from '@/lib/tiers';
import { noteSignedIn } from '@/lib/analytics';

/** The tier an admin is looking through (lib/tiers.ts). Theirs only: cleared on sign-out. */
export const TIER_LENS_KEY = 'copyfi.tierLens';

interface AppUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
}

interface AuthContextType {
  user: AppUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Admin rights on screen: false while an admin looks through a lower tier's lens. */
  isAdmin: boolean;
  /** The signed-in person IS an admin, whatever lens is on. Only the lens switch reads this. */
  isRealAdmin: boolean;
  /**
   * The tier the screens follow. Null = nothing is locked: the paywall is not
   * switched on and no lens is set. See lib/tiers.ts.
   */
  tier: Tier | null;
  /** The lens an admin picked on the header badge; null = their own view. */
  tierLens: Tier | null;
  setTierLens: (t: Tier | null) => void;
  /** May the screen let this person use a feature? Locked features stay visible. */
  can: (feature: Feature) => boolean;
  token: string | null;
  login: () => void;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

// Exported so a test or a local preview can stand in for a signed-in account.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded: userLoaded } = useUser();
  const { isSignedIn, getToken: clerkGetToken } = useClerkAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  /*
   * WHO THE SCREEN IS ABOUT, WHEN IT IS SOMEONE ELSE. 2026-09-21.
   *
   * Opened from the admin dashboard with ?viewAs=<id>, every data request
   * already answered for that person -- but `user` was still the signed-in
   * admin, so the header showed the ADMIN'S email, and everything keyed on
   * user.id (preferences, the notification marker, the tour) was the admin's.
   * Guillaume: "Make sure what I see is TRULY what HE sees on every level."
   *
   * So in that mode `user` IS the viewed person: their id, and the email and
   * name their account holds, read from the admin user record. isAdmin stays
   * false, as it already was, so the screen is the one a user sees.
   */
  const viewAsId = getViewAsUserId();
  const viewed = useQuery({
    queryKey: ['view-as-profile', viewAsId],
    enabled: !!viewAsId,
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      fetch(`/api/v1/admin/user?user_id=${encodeURIComponent(viewAsId!)}`).then((r) => r.json()),
  });

  const user: AppUser | null = useMemo(() => {
    if (viewAsId) {
      const name: string = (viewed.data?.name ?? '').trim();
      const [first, ...rest] = name ? name.split(/\s+/) : [];
      return {
        id: viewAsId,
        email: viewed.data?.email ?? null,
        firstName: first ?? null,
        lastName: rest.length ? rest.join(' ') : null,
        imageUrl: null,
      };
    }
    if (!clerkUser) return null;
    /* Set the id the API client reads BEFORE any child effect can fire a
       request. This used to happen only in the useEffect below -- and a
       PARENT'S EFFECT RUNS AFTER ITS CHILDREN'S. So the first requests after
       sign-in went out with no X-User-ID header, the backend fell back to
       "dev-user" (handlers.go:72), and the reply was an empty list that React
       Query cached as a SUCCESS. No error, no spinner, no retry: just $0.00 and
       0 trades, indistinguishable from an account with nothing in it.

       That is the whole bug behind both reports. The notification feed polls
       every 30s, so its SECOND poll picked up the real data -- Guillaume timed
       23 seconds. The sessions query has no interval, so it stayed empty until
       something refetched it, which is exactly what visiting My Sessions does.

       Assigning during render rather than in an effect is deliberate: it is an
       idempotent write to a module singleton, and it has to be true before the
       first render's effects run. 2026-09-03. */
    setClerkUserId(clerkUser.id);
    return {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress ?? null,
      firstName: clerkUser.firstName,
      lastName: clerkUser.lastName,
      imageUrl: clerkUser.imageUrl,
    };
  }, [clerkUser, viewAsId, viewed.data]);

  // Kept as a backstop for the case the memo is skipped on a later render.
  // The assignment above is the one that matters for the first request.
  // The SIGNED-IN person's id, always: requests read ?viewAs= on their own.
  useEffect(() => {
    if (clerkUser?.id) {
      setClerkUserId(clerkUser.id);
      // Where a NEW account came from, posted once (lib/analytics.ts).
      noteSignedIn(clerkUser.id, clerkUser.createdAt);
      // PostHog learns who this is (account id only). Missing until 10-25: every
      // web visit was anonymous, so no person profiles, funnels or replays by user.
      identifyPostHog(clerkUser.id);
    }
  }, [clerkUser?.id, clerkUser?.createdAt]);

  // Check admin role from Clerk public metadata OR hardcoded admin list
  const isRealAdmin = useMemo(() => {
    /****************
     * NOT AN ADMIN WHILE LOOKING THROUGH SOMEBODY ELSE'S EYES.
     *
     * Guillaume, 2026-09-06: "make sure that what WE see when we click 'Open
     * their screens' is 1:1 what they see."
     *
     * It was not. The iframe swaps the user id the API is asked about, but the
     * Clerk session in that document is still the admin's -- so `isAdmin` stayed
     * true and four things diverged from the real account:
     *
     *   TradingSidebar   two admin-only nav items they do not have
     *   MainLayout       an admin badge, and "Role: Administrator"
     *   useDashboardData withoutSimTraders(rows, isAdmin) returns rows UNFILTERED
     *                    for an admin, so the trader list included SIM_ traders
     *                    the user cannot see at all
     *
     * The last one is not chrome. It means the list of traders on screen was a
     * different list from theirs, which is exactly the thing this feature exists
     * to rule out: you cannot debug "which trader did you tap" against a page
     * showing traders they do not have.
     *
     * Turned off here rather than at the four call sites, because a fifth will
     * be added and nobody will remember this one. Impersonation is read-only
     * anyway -- fetchApi refuses every non-GET -- so nothing is lost by dropping
     * the role along with the identity.
     ****************/
    if (isViewingAsSomeoneElse()) return false;

    if (!clerkUser) return false;
    const metadata = clerkUser.publicMetadata as Record<string, unknown>;
    if (metadata?.role === 'admin') return true;

    // The email allowlist (lib/admins.ts), until Clerk metadata is set for all admins.
    return isAdminEmail(clerkUser.primaryEmailAddress?.emailAddress);
  }, [clerkUser]);

  /****************
   * THE TIER LENS. Guillaume, 2026-09-26: "I should be able to click on [Admin]
   * and change it for any of the 5 options and see what this level of user
   * would see."
   *
   * Admins only, and never while viewing as someone else (that screen is
   * already exactly theirs). Looking through a lower tier also drops the admin
   * extras -- the Activity page, simulated traders, the venue switch -- because
   * a Free user has none of them; isAdmin below is what every screen reads.
   ****************/
  const [lensRaw, setLensRaw] = useState<Tier | null>(() => {
    try {
      const v = localStorage.getItem(TIER_LENS_KEY);
      return isTier(v) ? v : null;
    } catch { return null; }
  });
  // An admin's speed run sees what a new member sees: Free, no admin extras (lib/speedRun.ts).
  const tierLens = isRealAdmin && !isViewingAsSomeoneElse() ? (inSpeedRun() ? 'free' : lensRaw) : null;
  const setTierLens = useCallback((t: Tier | null) => {
    setLensRaw(t);
    try {
      if (t && t !== 'admin') localStorage.setItem(TIER_LENS_KEY, t);
      else localStorage.removeItem(TIER_LENS_KEY);
    } catch { /* no storage: the lens lasts until reload */ }
  }, []);
  const isAdmin = isRealAdmin && (tierLens === null || tierLens === 'admin');

  /*
   * The account's own tier, from the server. Until the paywall is switched on
   * (`enforced`), it locks nothing; a server without the endpoint yet is the
   * same as "not switched on".
   */
  const entitlements = useQuery({
    queryKey: ['entitlements', user?.id],
    enabled: !!isSignedIn && !!user?.id,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: () => entitlementsApi.get(),
  });
  const serverTier = entitlements.data?.enforced && isTier(entitlements.data.tier) ? entitlements.data.tier : null;
  // An admin sees everything unless they pick a lens (paywall on, 2026-10-25).
  const tier: Tier | null = tierLens ?? (isRealAdmin ? null : serverTier);
  const can = useCallback((feature: Feature) => tier === null || tierAllows(tier, feature), [tier]);

  const queryClient = useQueryClient();

  const login = () => {
    navigate('/auth');
  };

  /****************
   * SIGNING OUT HAS TO EMPTY THE CACHE, NOT ONLY THE SESSION.
   *
   * logout called Clerk's signOut and nothing else. Every cached response --
   * sessions, trades, PnL, broker credentials -- survived it in the React
   * Query cache and was served to whoever signed in next on that browser,
   * until each query happened to refetch.
   *
   * On a personal laptop that is untidy. The beta is being demoed on shared
   * machines, where it means the next person to sign in sees the last person's
   * account while their own data loads.
   *
   * clear() rather than invalidateQueries: invalidation marks data stale and
   * keeps serving it while it refetches, which is the precise behaviour that
   * has to stop.
   ****************/
  const logout = async () => {
    track('signed_out');
    await signOut();
    queryClient.clear();
    identifyPostHog(null); // the next person on this browser is someone else
    /*
     * And forget WHO. Every request identifies itself from this global
     * (api/client.ts), and it stayed set until the next person signed in --
     * so a background poll between sign-out and sign-in still went out as
     * the previous user. M-23, 2026-09-19.
     */
    delete (window as any).__clerk_user_id;
    /*
     * And forget what the BROWSER kept. The query cache above is memory; the
     * Orb conversation and the notification read-marker are on disk, and on a
     * shared machine the next person to sign in could read the previous
     * person's chat. M-23, 2026-09-20.
     */
    forgetPersonalBrowserState();
  };

  const getToken = async (): Promise<string | null> => {
    try {
      return await clerkGetToken();
    } catch {
      return null;
    }
  };

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isLoading: !userLoaded,
      isAuthenticated: !!isSignedIn,
      isAdmin,
      isRealAdmin,
      tier,
      tierLens,
      setTierLens,
      can,
      token: null, // Use getToken() for async token retrieval
      login,
      logout,
      getToken,
    }),
    [user, userLoaded, isSignedIn, isAdmin, isRealAdmin, tier, tierLens, setTierLens, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
